/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef, useCallback, memo } from "react";
import { MultiProductCardProps } from "../../Services/CommonServices/propTypes";
import { useSelector } from 'react-redux';
import MultiProductLoaderCard from "./MultiProductLoaderCard";

// Wrap with memo to prevent unnecessary re-renders
const MultiProductCard = memo(function MultiProductCard({
  productIndex,
  product,
  products,
  cardLength,
  isAnswerComplete,
  setIsProductLoading,
  effects,
  categoryListing,
  stopHover,
  stopClick
}) {
  const loading = useSelector((state) => state.common.isLoader);

  // Move mutable variables to useRef to persist between renders without causing re-renders
  const dataRef = useRef({
    categoryObjectMapArray: [],
    onlyCats: [],
    processedProductId: null
  });

  // Memoize functions to prevent recreation on every render
  const getClassName = useCallback((index) => {
    if (index === 0) return "coat1 animation-coat";
    if (index === 1) return "shirt1 animation-shirt";
    if (index === 2) return "pant1 animation-pant";
    if (index === 3) return "watch1 animation-watch";
  }, []);

  const slotHierarchy = {
    slot0: [
      "OUTERWEAR",
      "JACKET",
      "TAILORED CLOTHING",
      "DRESSES",
      "SWEATER",
      "SHIRTS",
      "TOPS",
      "KNIT SHIRTS",
      "SWIMWEAR",
      "ONE PIECE",
      "INTIMATE",
      "SLEEPWEAR",
    ],
    slot1: [
      "TAILORED CLOTHING",
      "DRESSES",
      "SWEATER",
      "SHIRTS",
      "TOPS",
      "KNIT SHIRTS",
      "BOTTOMS",
      "SHORTS",
      "SWIMWEAR",
      "ONE PIECE",
      "JEANS",
      "PANTS",
      "SKIRT",
      "SKORTS",
      "INTIMATE",
      "SLEEPWEAR",
      "BELTS",
      "NECKWEAR",
      "HEADWEAR",
      "BAGS",
      "EYEWEAR",
      "FINE JEWELRY",
      "FASHION JEWELRY",
      "HOSIERY",
      "SCARF",
      "GLOVES",
      "SMALL LEATHERGOODS",
      "APPAREL ACCESSORIES",
      "SMALL ACCESSORIES",
    ],
    slot2: [
      "TAILORED CLOTHING",
      "SWEATER",
      "SHIRTS",
      "TOPS",
      "KNIT SHIRTS",
      "BOTTOMS",
      "SHORTS",
      "SWIMWEAR",
      "ONE PIECE",
      "JEANS",
      "PANTS",
      "SKIRT",
      "SKORTS",
      "INTIMATE",
      "SLEEPWEAR",
      "BELTS",
      "NECKWEAR",
      "HEADWEAR",
      "BAGS",
      "EYEWEAR",
      "FINE JEWELRY",
      "FASHION JEWELRY",
      "HOSIERY",
      "SCARF",
      "GLOVES",
      "SMALL LEATHERGOODS",
      "APPAREL ACCESSORIES",
      "SMALL ACCESSORIES",
    ],
    slot3: [
      "BAGS",
      "FOOTWEAR",
      "BOOTS",
      "SHOE",
      "SNEAKERS",
      "SANDALS",
      "SLIP-ONS",
      "FLATS",
      "DRESS SHOE",
      "CASUAL SHOE",
      "ESPADRILLES",
      "CLOG",
      "PUMPS",
      "BELTS",
      "NECKWEAR",
      "HEADWEAR",
      //"BAGS",
      "EYEWEAR",
      "FINE JEWELRY",
      "FASHION JEWELRY",
      "HOSIERY",
      "SCARF",
      "GLOVES",
      "SMALL LEATHERGOODS",
      "APPAREL ACCESSORIES",
      "SMALL ACCESSORIES",
    ],
  };

  const [slotsData, setSlotsData] = useState([]);
  const [allSlotsReady, setAllSlotsReady] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Use useCallback to prevent recreating this function on every render
  const fetchImage = useCallback((
    slot,
    data,
    hierarchy
  ) => {
    // Use the ref values
    const { categoryObjectMapArray, onlyCats } = dataRef.current;

    let finaldata;

    if (slot === 0) {
      // Clear arrays on first slot
      categoryObjectMapArray.length = 0;
      onlyCats.length = 0;

      data.forEach((element) => {
        categoryObjectMapArray.push({
          cat: element.productCategory,
          element: element,
        });
        onlyCats.push(element.productCategory);
      });
    }

    for (const category of hierarchy) {
      if (onlyCats.includes(category)) {
        finaldata = categoryObjectMapArray.find(
          (item) => item.cat === category
        );
        const prodIndex = onlyCats.findIndex((item) => item === category);
        onlyCats.splice(prodIndex, 1);
        categoryObjectMapArray.splice(prodIndex, 1);
        break;
      }
    }

    // Batch state updates to reduce renders
    setSlotsData(prev => {
      const newData = [...prev, { "element": finaldata?.element || null, "slot": slot }];
      // Only set allSlotsReady when all slots are processed
      if (newData.length === 4) {
        // Use timeout to batch this state update
        setTimeout(() => setAllSlotsReady(true), 0);
      }
      return newData;
    });
  }, []);

  const imageTemplate = useCallback((productData, prodIndex) => {
    if (!productData) return null;

    const primaryImage = productData.media.find(imageUrl => imageUrl.type === "PrimaryImage");
    if (!primaryImage) return null;

    return (
      <img
        src={
          primaryImage.url.split("?fmt=webp-alpha")[0] +
          "?$rl_5x7_pdp$&fmt=webp-alpha"
        }
        alt={productData.name}
        data-testid={`product-image-${prodIndex}`}
        className={`img-fluid ${getClassName(prodIndex)}`}
        onError={(e) => {
          console.log("Image failed to load:", e);
        }}
      />
    );
  }, [getClassName]);

  const words = product.name;

  // Handle completion status
  useEffect(() => {
    if (isAnswerComplete && products.length === productIndex + 1) {
      setIsProductLoading(true);
    }
  }, [isAnswerComplete, products, productIndex, setIsProductLoading]);

  // Process products - fix dependency to 'product' instead of 'products'
  useEffect(() => {
    if (!product?.products) return;

    // Skip processing if we've already processed this product
    if (dataRef.current.processedProductId === product.id) return;
    dataRef.current.processedProductId = product.id;

    // Reset states for new product
    setSlotsData([]);
    setAllSlotsReady(false);
    setImagesLoaded(false);

    const slotsArray = [0, 1, 2, 3];

    // Process all slots in batch
    slotsArray.forEach((element, index) => {
      fetchImage(index, product.products, slotHierarchy[`slot${index}`]);
    });
  }, [product, fetchImage, slotHierarchy]); // Note: correctly depends on product not products

  // Image preloading logic
  useEffect(() => {
    if (!allSlotsReady || slotsData.length !== 4) return;

    const imagesToPreload = [];

    slotsData.forEach(slotItem => {
      if (slotItem.element && slotItem.element.media) {
        const primaryImage = slotItem.element.media.find(img => img.type === "PrimaryImage");
        if (primaryImage) {
          const imageUrl = primaryImage.url.split("?fmt=webp-alpha")[0] + "?$rl_5x7_pdp$&fmt=webp-alpha";
          imagesToPreload.push(imageUrl);
        }
      }
    });

    if (imagesToPreload.length === 0) {
      setImagesLoaded(true);
      return;
    }

    setImagesLoaded(false);

    const preloadPromises = imagesToPreload.map(url => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = url;
        img.onload = resolve;
        img.onerror = resolve;
      });
    });

    Promise.all(preloadPromises).then(() => {
      setImagesLoaded(true);
    });
  }, [allSlotsReady, slotsData]);

  // Memoize the card content to prevent unnecessary renders
  const cardContent = React.useMemo(() => (
    <div>
      {product?.loader ? (
        <div className="loader-card position-absolute d-flex">
          <MultiProductLoaderCard
            key={"loader" + productIndex}
            loaderImage={product.url}
            loaderIndex={productIndex}
            loaderFallbackImage={product.fallbackUrl}
          />
        </div>
      ) : (
        <div>
          <div>
            <div className="content-title_container col-12 justify-self-center">
              <span className="content-title content-title-fade">
                {words}
              </span>
              <span className="textLook col-12 justify-self-center">
                {"Look " + `${productIndex + 1}` + " of " + cardLength}
              </span>
            </div>

            {allSlotsReady && imagesLoaded ? (
              <div className="product-images-container">
                {slotsData.map((item, index) => (
                  <div key={index} className="product-image-wrapper">
                    {imageTemplate(item.element, item.slot)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="products-loading-placeholder">
              </div>
            )}
            {!stopClick && (
              <button name="viewLook" className={`bottomTextContainer px-0 ${allSlotsReady && imagesLoaded ? 'animate' : ''}`}>
                <span className="viewText">+ View Look</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  ), [product, productIndex, words, cardLength, allSlotsReady, imagesLoaded, slotsData, imageTemplate]);

  return (
    <button
      name="looksCard"
      style={{ animationDelay: !effects ? `${productIndex * 1}s` : '' }}
      className={`box d-flex px-0 ${stopHover ? "noHover" : "effectHover"
        } col-7 col-md-4 fade-in bg-fade-in  ${productIndex === 0 && "mcat-box-ms"}`}
      onClick={categoryListing}
      disabled={loading}
    >
      {cardContent}
    </button>
  );
});

MultiProductCard.propTypes = MultiProductCardProps;

export default MultiProductCard;
